import { call, put, takeLatest } from "redux-saga/effects";
import * as types from "./LoginActionType";
import * as actions from "./LoginActions";
import * as authService from "./LoginServices";
import { deleteCookie, setCookie } from "../../../store/localStorage";
import { LOGIN_STATUS, LOGIN_MESSAGES } from "../../../constants/loginConstants";

function* loginSaga(action) {
  try {
    const { isCustomLogin, EmailAddress, Password, instance, accounts } =
      action.payload;

    if (isCustomLogin) {
      const response = yield call(authService.customLogin, {
        EmailAddress,
        Password,
      });

      // Backend returns ResponseData<T> wrapper — extract the inner Value
      const data = response?.Value || response?.value || response?.values || response?.data || response;

      // Authenticator (TOTP) challenge — don't set cookies yet
      // Backend sends PascalCase: IsMfaEnabled, RequiresMfaSetup, RequiresMfa
      if (data?.IsMfaEnabled || data?.isMfaEnabled) {
        if (data?.RequiresMfaSetup || data?.requiresMfaSetup) {
          yield put(actions.mfaSetupRequired(data));
        } else if (data?.RequiresMfa || data?.requiresMfa) {
          yield put(actions.mfaRequired(data));
        }
        return;
      }

      if (!data?.TKN && !data?.tkn) {
        const statusId = data?.StatusId ?? data?.statusId;
        const errMsg =
          statusId === LOGIN_STATUS.DIFFERENT_METHOD ? LOGIN_MESSAGES.DIFFERENT_METHOD :
          statusId === LOGIN_STATUS.INACTIVE ? LOGIN_MESSAGES.ACCOUNT_INACTIVE :
          statusId === LOGIN_STATUS.DELETED  ? LOGIN_MESSAGES.ACCOUNT_DELETED :
          statusId === LOGIN_STATUS.NO_ROLE  ? LOGIN_MESSAGES.ROLE_NOT_DEFINED :
          response?.ErrorMessage || response?.errorMessage ||
          LOGIN_MESSAGES.INVALID_CREDENTIALS;
        yield put(actions.loginFailure({ message: errMsg }));
        return;
      }

      const cookieExpiry = data.JwtExpirationTime || data.jwtExpirationTime;
      yield call(setCookie, "authToken", data.TKN || data.tkn, cookieExpiry);
      yield call(setCookie, "userData", data.userDetailModel, cookieExpiry);
      yield call(setCookie, "jwtExpirationTime", cookieExpiry, cookieExpiry);
      yield put(actions.loginSuccess(data.userDetailModel));
    } else {
      // Microsoft Entra ID (SSO)
      const tokenResponse = yield call(authService.handleMicrosoftLogin, {
        instance,
        accounts,
      });
      const payload = {
        UserName: tokenResponse?.account?.name,
        EmailAddress: tokenResponse?.account?.username,
        // Send idToken for backend validation
        SsoToken: tokenResponse?.idToken || tokenResponse?.accessToken || "",
        ServerUrl: window.location.href,
      };
      const response = yield call(authService.login, payload);
      const data = response?.Value || response?.value || response?.values || response?.data || response;

      if (!data?.TKN && !data?.tkn) {
        // Backend returned no token — login did not fully succeed
        const statusId = data?.StatusId ?? data?.statusId;

        // Backend can return StatusId=NO_ROLE with Success=true as an informational, non-error result.
        if (statusId === LOGIN_STATUS.NO_ROLE && (response?.Success === true || response?.success === true)) {
          yield put(actions.loginInfo({ message: response?.ResultMessage || response?.resultMessage || LOGIN_MESSAGES.NO_ROLE_ASSIGNED }));
          return;
        }

        const errMsg =
          statusId === LOGIN_STATUS.DIFFERENT_METHOD ? LOGIN_MESSAGES.DIFFERENT_METHOD :
          response?.ErrorMessage || response?.errorMessage ||
          (statusId === LOGIN_STATUS.NO_ROLE  ? LOGIN_MESSAGES.NO_ROLE_ASSIGNED :
           statusId === LOGIN_STATUS.INACTIVE ? LOGIN_MESSAGES.ACCOUNT_INACTIVE :
           LOGIN_MESSAGES.SSO_FAILED);
        yield put(actions.loginFailure({ message: errMsg }));
        return;
      }

      const cookieExpiry = data.JwtExpirationTime || data.jwtExpirationTime;
      yield call(setCookie, "authToken", data.TKN || data.tkn, cookieExpiry);
      yield call(setCookie, "userData", data.userDetailModel, cookieExpiry);
      yield call(setCookie, "jwtExpirationTime", cookieExpiry, cookieExpiry);
      yield put(actions.loginSuccess(data.userDetailModel));
    }
  } catch (error) {
    yield put(actions.loginFailure(error));
  }
}

function* verifyMfaSaga(action) {
  try {
    const response = yield call(authService.verifyMfa, action.payload);
    const data = response?.Value || response?.value || response?.values || response?.data || response;

    if (data?.success === false || data?.Success === false || !data?.TKN) {
      yield put(actions.verifyMfaFailure({ message: data?.errorMessage || "MFA verification failed" }));
      return;
    }

    const cookieExpiry = data.JwtExpirationTime || data.jwtExpirationTime;
    yield call(setCookie, "authToken", data.TKN || data.tkn, cookieExpiry);
    yield call(setCookie, "userData", data.userDetailModel, cookieExpiry);
    yield call(setCookie, "jwtExpirationTime", cookieExpiry, cookieExpiry);
    yield put(actions.verifyMfaSuccess(data.userDetailModel));
    yield put(actions.loginSuccess(data.userDetailModel));
  } catch (error) {
    yield put(actions.verifyMfaFailure(error));
  }
}

function* logoutSaga() {
  try {
    yield call(deleteCookie, "jwtExpirationTime");
    yield call(deleteCookie, "authToken");
    yield call(deleteCookie, "userData");
    yield put(actions.logoutSuccess());
  } catch (error) {
    yield put(actions.logoutFailure(error));
  }
}

export default function* watchAuthSaga() {
  yield takeLatest(types.LOGIN_REQUEST, loginSaga);
  yield takeLatest(types.LOGOUT_REQUEST, logoutSaga);
  yield takeLatest(types.VERIFY_MFA_REQUEST, verifyMfaSaga);
}
